import { AppSettingContext, defaultTripContextData, LoggingContext, LoggingContextData, NavigationEntryContext, NavigationEntryContextData, TripContext, TripContextData } from 'contexts';
import { DbState, useDbState } from 'contexts/db-state';
import React, { FC } from 'react';
import { AppSetting, CatchWeight, Company, EquipmentShortReason, EquipmentType, SyncStatus, FilteredLicensePlate, LicensePlate, LicensePlateItem, LineItem, LineItemBarcode, Mispick, NavigationEntry, NavigationRoute, Order, OrderItem, OrderItemChange, OrderLicensePlate, ReconcileReason, RefusalReason, ReturnChangeReason, ReturnItem, Signature, Stop, StopEquipment, Trip, VerifyMultipleItemsReason, OrderItemChangeImage } from 'services/types';
import { CompanyEmail } from 'services/types/companyEmail';
import { DbOperation } from 'types';

export const AppContexts: FC = ({children}) => {
    
    const appSettingContextValue = () => {
        const appSettings: DbState<AppSetting> = useDbState<AppSetting>(undefined, 'setting')
        return {
            appSettings: appSettings,
            isReady: () => appSettings.isReady()
        }
    }

    const navigationEntryContextValue = (): NavigationEntryContextData => {
        const entries: DbState<NavigationEntry> = useDbState<NavigationEntry>(undefined, 'navigationStack')
        const navigationRoutes: DbState<NavigationRoute> = useDbState<NavigationRoute>(undefined, 'navigationRoute')
        return {
            navigationEntries: entries,
            navigationRoutes: navigationRoutes,
            isReady: () => entries.isReady()
        }
    }

    const tripContextValue = () => {
        const states: TripContextData = {
            ...defaultTripContextData,
            trips: useDbState<Trip>(undefined, 'trip'),
            stops: useDbState<Stop>(undefined, 'stop'),
            stopEquipments: useDbState<StopEquipment>(undefined, 'stopEquipment'),
            equipmentTypes: useDbState<EquipmentType>(undefined, 'equipmentType'),
            equipmentShortReasons: useDbState<EquipmentShortReason>(undefined, 'palletShortReason'),
            orderItems: useDbState<OrderItem>(undefined, 'orderItem'),
            orders: useDbState<Order>(undefined, 'order'),
            lineItems: useDbState<LineItem>(undefined, 'lineItem'),
            lineItemBarcodes: useDbState<LineItemBarcode>(undefined, 'lineItemBarcode'),
            signatures: useDbState<Signature>(undefined, 'signature'),
            mispicks: useDbState<Mispick>(undefined, 'mispicks'),
            licensePlateItems: useDbState<LicensePlateItem>(undefined, 'licensePlateItem'),
            orderItemChanges: useDbState<OrderItemChange>(undefined, 'orderItemChange'),
            licensePlates: useDbState<LicensePlate>(undefined, 'licensePlate'),
            catchWeights: useDbState<CatchWeight>(undefined, 'catchWeight'),
            orderLicensePlates: useDbState<OrderLicensePlate>(undefined, 'orderLicensePlate'),
            reconcileReasons: useDbState<ReconcileReason>(undefined, 'reconcileReason'),
            refusalReasons: useDbState<RefusalReason>(undefined, 'refusalReason'),
            companies: useDbState<Company>(undefined, 'company'),
            verifyMultipleItemsReasons: useDbState<VerifyMultipleItemsReason>(undefined, 'verifyMultipleItemsReason'),
            returnItems: useDbState<ReturnItem>(undefined, 'returnItem'),
            returnChangeReasons: useDbState<ReturnChangeReason>(undefined, 'returnChangeReason'),
            syncStatus : useDbState<SyncStatus>(undefined, 'syncStatus'),
            filteredLicensePlates: useDbState<FilteredLicensePlate>(undefined, 'filteredLicensePlates'),
            orderItemChangeImages: useDbState<OrderItemChangeImage>(undefined, 'orderItemChangeImage'),
            companyEmail: useDbState<CompanyEmail>(undefined,'companyEmail'),
            companyEmailAdditions: useDbState<CompanyEmail>(undefined,'companyEmailAdditions'),
            companyEmailDeletions: useDbState<CompanyEmail>(undefined,'companyEmailDeletions'),
        }
    
        return {
          ...states,
          isReady: (): boolean => {
            let ready: boolean = true
            for (const stateName in states) {
              const state: DbState<any> = states[stateName] as any
              if (state.isReady && !state.isReady()) {
                ready = false
                break
              }
            }
            return ready
          }
        }
    }

    const loggingContextValue = (): LoggingContextData => {
        const operations: DbState<DbOperation> = useDbState<DbOperation>(undefined, 'dbOperations')
        return {
            dbOperations: operations,
            isReady: () => operations.isReady()
        }
    }

    return (
        <NavigationEntryContext.Provider value={navigationEntryContextValue()}>
            <AppSettingContext.Provider value={appSettingContextValue()}>
                <TripContext.Provider value={tripContextValue()}>
                    <LoggingContext.Provider value={loggingContextValue()}>
                        {children}
                    </LoggingContext.Provider>
                </TripContext.Provider>
            </AppSettingContext.Provider>
        </NavigationEntryContext.Provider>
    )
}